import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import SideBar from './SideBar';
import TopBar from './TopBar';
import AssignmentAlerts from './AssignmentAlerts/AssignmentAlerts';
import { MessageToastHost } from './MessageNotifications';

const COLLAPSED_KEY = 'sidebarCollapsed';

const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch (error) {
    return false;
  }
};

/**
 * Portal shell (Tailwind): sidebar, top bar and the page. Pages are still
 * mostly legacy CSS; the content area keeps the legacy page background (body)
 * so they look as before.
 *
 * Don't give the content column a transform, filter, opacity or z-index: pages
 * render fixed modals inline and those would get trapped under the sidebar.
 */
const Layout = ({ children, pageTitle = '' }) => {
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0');
      } catch (error) {
        // Storage may be unavailable (private mode); the toggle still works.
      }
      return next;
    });
  }, []);

  const openMobileNav = useCallback(() => setMobileNavOpen(true), []);
  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);

  // Close the mobile drawer on navigation and on Escape
  useEffect(() => {
    setMobileNavOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setMobileNavOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileNavOpen]);

  return (
    <div className="tw-min-h-screen tw-w-full">
      <SideBar
        collapsed={collapsed}
        onToggleCollapse={toggleCollapsed}
        mobileOpen={mobileNavOpen}
        onCloseMobile={closeMobileNav}
      />

      <div
        className={`tw-flex tw-min-h-screen tw-min-w-0 tw-flex-col tw-transition-[padding] tw-duration-300 tw-ease-out ${
          collapsed ? 'md:tw-pl-[6.25rem]' : 'md:tw-pl-[18rem]'
        }`}
      >
        <TopBar pageTitle={pageTitle} onOpenMobileNav={openMobileNav} />
        <main className="tw-min-w-0 tw-flex-1 tw-overflow-x-clip tw-px-3 tw-pb-6 tw-pt-4 sm:tw-px-5 md:tw-pl-0">
          {/* Son tamamlama tarihi yaklaşan atamalar için kırmızı uyarı bandı ve
              girişte bir kez çıkan birleşik hatırlatma penceresi (okunmamış
              SOP'lar + eksik CV/GCP/IATA belgeleri) */}
          <AssignmentAlerts />
          {children}
        </main>
      </div>

      {/* Gelen mesaj kartları: kullanıcı hangi sayfada olursa olsun görünür */}
      <MessageToastHost />
    </div>
  );
};

Layout.propTypes = {
  children: PropTypes.node,
  pageTitle: PropTypes.string,
};

export default Layout;
